/**
 * Página: Médicos que me atenderam (Paciente)
 * Histórico de atendimentos e acesso ao chat com os médicos.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Search, Bell } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types/auth';
import { Sidebar } from '@/components/common/Sidebar';
import { ThemeToggle } from '@/components/common';
import { AttendedDoctorsList } from '@/components/dashboard';

export default function MeusMedicosPage() {
  const navigate = useNavigate();
  const { user, isLoading, logout } = useAuth();

  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login');
    } else if (!isLoading && user && user.role !== UserRole.PATIENT) {
      navigate('/dashboard');
    }
  }, [user, isLoading, navigate]);

  if (isLoading || !user) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 dark:bg-neutral-950">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-teal-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-neutral-950">
      <Sidebar userRole={user.role} userName={user.name || ''} onLogout={logout} />

      <div className="flex-1 overflow-y-auto">
        <header className="sticky top-0 z-10 border-b border-gray-200 bg-white/80 px-6 py-4 backdrop-blur dark:border-neutral-800 dark:bg-neutral-900/80">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Médicos que me atenderam
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Histórico de atendimentos e conversas com seus médicos
              </p>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <button
                type="button"
                onClick={() => navigate('/notifications')}
                className="rounded-xl p-2 text-gray-500 transition-colors hover:bg-gray-100 dark:hover:bg-neutral-800"
                aria-label="Notificações"
              >
                <Bell className="h-5 w-5" />
              </button>
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-6xl space-y-6 p-6">
          <section className="rounded-xl border border-gray-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-900">
            <div className="mb-6 flex items-center gap-3">
              <div className="rounded-lg bg-teal-50 p-2 text-teal-600 dark:bg-teal-900/20">
                <MessageCircle className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Conversas e histórico
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Inicie ou continue o chat com médicos que já te atenderam
                </p>
              </div>
            </div>
            <AttendedDoctorsList />
          </section>

          <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-gray-500 dark:text-gray-400">
            <Search className="h-4 w-4" />
            <span>
              Não encontrou quem procura?{' '}
              <button
                type="button"
                onClick={() => navigate('/buscar-medicos')}
                className="font-semibold text-teal-600 hover:underline dark:text-teal-400"
              >
                Buscar médicos
              </button>
            </span>
          </div>
        </main>
      </div>
    </div>
  );
}
